import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function FeaturedIncubatees() {
  const [incubatees, setIncubatees] = useState([]);
  const [loading, setLoading] = useState(true);

  const scrollRef = useRef(null);
  const isPausedRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeftState, setScrollLeftState] = useState(0);

  useEffect(() => {
    const fetchStartups = async () => {
      try {
        const [res1, res2] = await Promise.all([
          fetch('https://tbido-website-api.azurewebsites.net/api/startups?cohort=1'),
          fetch('https://tbido-website-api.azurewebsites.net/api/startups?cohort=2')
        ]);
        
        const data1 = await res1.json();
        const data2 = await res2.json();
        
        const combined = [...data1, ...data2].filter(startup => startup.logoUrl);
        setIncubatees(combined);
      } catch (error) {
        console.error('Error fetching startups:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStartups();
  }, []);

  useEffect(() => {
    if (incubatees.length === 0) return;

    let animationId;
    const scrollNode = scrollRef.current;

    const step = () => {
      if (scrollNode && !isPausedRef.current && !isDragging) {
        scrollNode.scrollLeft += 1; 
        if (scrollNode.scrollLeft >= scrollNode.scrollWidth / 2) {
          scrollNode.scrollLeft -= scrollNode.scrollWidth / 2;
        }
      }
      animationId = requestAnimationFrame(step);
    };

    animationId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationId);
  }, [incubatees, isDragging]);

  const handleMouseDown = (e) => {
    isPausedRef.current = true;
    setIsDragging(true);
    setHasDragged(false);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeftState(scrollRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    isPausedRef.current = false;
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    isPausedRef.current = false;
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2; 
    
    if (Math.abs(walk) > 5) {
      setHasDragged(true);
    }
    scrollRef.current.scrollLeft = scrollLeftState - walk;
  };

  const handleTouchStart = () => {
    isPausedRef.current = true;
  };

  const handleTouchEnd = () => {
    isPausedRef.current = false;
  };

  if (loading || incubatees.length === 0) return null;

  const marqueeItems = [...incubatees, ...incubatees, ...incubatees, ...incubatees];

  return (
    <section className="py-12 bg-white overflow-hidden">
      <div className="w-[100%] max-w-6xl 2xl:max-w-5xl mx-auto px-4 sm:px-6 md:px-8">
        
        <div className="text-center mb-10" data-aos="fade-up">
          <h2 className="text-2xl sm:text-3xl font-light text-gray-900">
            Meet Our <span className="font-bold text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, rgba(109,12,34,1) 0%, rgba(14,56,107,1) 76%)' }}>Incubatees</span>
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mt-2 max-w-2xl mx-auto">
            Explore the innovative startups and growing ventures powered by our community.
          </p>
        </div>

        <style>
          {`
            .hide-scrollbar::-webkit-scrollbar {
              display: none;
            }
            .hide-scrollbar {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }
          `}
        </style>

        <div className="relative w-full py-4 rounded-2xl">
          <div 
            ref={scrollRef}
            onMouseEnter={() => (isPausedRef.current = true)}
            onMouseLeave={handleMouseLeave}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            className="flex gap-6 overflow-x-auto hide-scrollbar px-3 cursor-grab active:cursor-grabbing select-none"
          >
            {marqueeItems.map((startup, index) => (
              <Link
                to="/incubatees"
                key={`${startup._id || index}-${index}`}
                onClick={(e) => {
                  if (hasDragged) e.preventDefault();
                }}
                className="group/card relative w-56 h-36 flex-shrink-0 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl flex flex-col items-center justify-center p-4"
              >
                <img 
                  src={startup.logoUrl} 
                  alt={`${startup.name} logo`} 
                  draggable="false"
                  className="w-full h-full object-contain transition-transform duration-300 group-hover/card:scale-110"
                />
                
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover/card:opacity-100 transition-opacity duration-300 flex items-center justify-center p-4 text-center z-10">
                  <h3 className="text-base font-bold text-white tracking-wide">
                    {startup.name}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}